import { useState } from "react";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Alert,
  AlertDescription,
} from "@openzeppelin/ui-components";
import { Icon } from "@ascend/shared/components";
import { toast } from "sonner";
import { useWalletStore } from "../stores/useWalletStore";

// ─── Types ──────────────────────────────────────────────────────────────────────

interface LiquidationAccount {
  accountId: string;
  borrower: string;
  token: string;
  collateralAmount: number;
  collateralValue: number;
  outstandingDebt: number;
  healthFactor: number;
  timeInDefault: string;
  facility: string;
  findersFee: number;
  feeGuaranteed: boolean;
}

// ─── Deterministic mock helper ──────────────────────────────────────────────────

function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// ─── Mock Data ──────────────────────────────────────────────────────────────────

const TOKENS = ["ABN", "FMM", "SNG", "APX", "MSM", "SBC"];
const FACILITIES = [
  "USDC Core Facility",
  "ABN Yield Facility",
  "SNG Stable Facility",
  "Institutional ABN Prime",
  "FMM Reserve Facility",
  "MSM Income Facility",
];

function generateAccount(accountId: string): LiquidationAccount {
  const numPart = parseInt(accountId.replace(/\D/g, ""), 10) || 2008;
  const s = numPart;
  const tokenIdx = Math.floor(seededRandom(s * 3) * TOKENS.length);
  const facilityIdx = Math.floor(seededRandom(s * 7) * FACILITIES.length);
  const collateralAmount = +(seededRandom(s * 11) * 15 + 0.5).toFixed(4);
  const pricePerUnit = Math.floor(seededRandom(s * 13) * 80000 + 20000);
  const collateralValue = Math.round(collateralAmount * pricePerUnit);
  const debtRatio = seededRandom(s * 17) * 0.3 + 0.6;
  const outstandingDebt = Math.round(collateralValue * debtRatio);
  const healthFactor = +(collateralValue / outstandingDebt).toFixed(3);
  const days = Math.floor(seededRandom(s * 19) * 5);
  const hours = Math.floor(seededRandom(s * 23) * 24);

  const addrSeed = s * 29;
  const borrower = `0x${Math.floor(seededRandom(addrSeed) * 0xffffff)
    .toString(16)
    .padStart(6, "0")}...${Math.floor(seededRandom(addrSeed + 1) * 0xffff)
    .toString(16)
    .padStart(4, "0")}`;

  const feeBase = Math.round(
    outstandingDebt * (0.001 + seededRandom(s * 31) * 0.004),
  );
  const feeGuaranteed = seededRandom(s * 37) > 0.55;

  return {
    accountId,
    borrower,
    token: TOKENS[tokenIdx],
    collateralAmount,
    collateralValue,
    outstandingDebt,
    healthFactor,
    timeInDefault: `${days}d ${hours}h`,
    facility: FACILITIES[facilityIdx],
    findersFee: feeBase,
    feeGuaranteed,
  };
}

const WALLET_BALANCE = 150000;

// ─── Page ───────────────────────────────────────────────────────────────────────

export function LiquidationExecutePage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isVerifiedBuyerFlow = searchParams.get("mode") === "verified-buyer";
  const { isConnected, connect } = useWalletStore();

  const account = generateAccount(accountId ?? "ma-2008");
  const profitLoss = account.collateralValue - account.outstandingDebt;
  const hasEnoughBalance = WALLET_BALANCE >= account.outstandingDebt;
  const shortfall = hasEnoughBalance
    ? 0
    : account.outstandingDebt - WALLET_BALANCE;

  const [approveState, setApproveState] = useState<string>("idle");
  const [liquidateState, setLiquidateState] = useState<string>("idle");
  const [txHash] = useState(
    () => `0x${Math.floor(Math.random() * 0xffffffffffff).toString(16)}...`,
  );

  function handleApproveVerified() {
    setApproveState("loading");
    setTimeout(() => {
      setApproveState("done");
      toast.success("USDC spending approved");
    }, 2000);
  }

  function handleLiquidateSearcher() {
    setLiquidateState("loading");
    setTimeout(() => {
      setLiquidateState("done");
      toast.success(
        `Liquidation of ${account.accountId} triggered — any finder's fee pays out after the DDF auction settles.`,
      );
    }, 3000);
  }

  function handleLiquidateVerified() {
    if (approveState !== "done") return;
    setLiquidateState("loading");
    setTimeout(() => {
      setLiquidateState("done");
      toast.success(`Liquidation of ${account.accountId} executed`);
    }, 3000);
  }

  if (liquidateState === "done") {
    if (isVerifiedBuyerFlow) {
      return (
        <div className="mx-auto max-w-md py-16 text-center space-y-6">
          <div className="mx-auto bg-green-100 p-4 rounded-full w-fit">
            <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-10 w-10 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Liquidation Executed</h2>
            <p className="text-muted-foreground mt-2">
              Direct liquidation completed for {account.accountId}. You acquired
              collateral by repaying outstanding debt on the credit facility.
            </p>
          </div>
          <div className="rounded-xl border border-border p-4 text-left space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Collateral Acquired</span>
              <span className="font-medium">
                {account.collateralAmount} {account.token}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Debt Repaid</span>
              <span className="font-medium">
                ${account.outstandingDebt.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Est. surplus</span>
              <span className="font-medium text-green-600">
                +${profitLoss.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tx Hash</span>
              <span className="font-mono text-xs text-primary">
                {txHash}
              </span>
            </div>
          </div>
          <Button onClick={() => navigate("/liquidations")}>
            Back to Liquidations
          </Button>
        </div>
      );
    }

    return (
      <div className="mx-auto max-w-md py-16 text-center space-y-6">
        <div className="mx-auto bg-green-100 p-4 rounded-full w-fit">
          <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-10 w-10 text-green-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold">Liquidation Triggered</h2>
          <p className="text-muted-foreground mt-2">
            You triggered the liquidation of account {account.accountId}. Any
            finder&apos;s fee is paid to your wallet after the DDF auction
            settles—not at transaction time.
          </p>
        </div>
        <div className="rounded-xl border border-border p-4 text-left space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Account Liquidated</span>
            <span className="font-medium">{account.accountId}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Collateral Seized</span>
            <span className="font-medium">
              {account.collateralAmount} {account.token}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              Est. finder&apos;s fee (after auction)
            </span>
            <span className="font-medium text-green-600">
              +${account.findersFee.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Tx Hash</span>
            <span className="font-mono text-xs text-primary">
              {txHash}
            </span>
          </div>
        </div>
        <Button onClick={() => navigate("/liquidations")}>
          Back to Opportunities
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Back link */}
      <Link
        to="/liquidations"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
        Back to Opportunities
      </Link>

      {/* Page header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold">
          {isVerifiedBuyerFlow
            ? "Direct liquidation (verified buyer)"
            : "Trigger Liquidation"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isVerifiedBuyerFlow ? (
            <>
              You passed this credit facility&apos;s compliance. Execute a
              direct liquidation on account {account.accountId} - this path
              bypasses DDF auctions.
            </>
          ) : (
            <>
              Trigger the DDF liquidation for account {account.accountId}; a
              finder&apos;s fee, if configured, is paid after the auction
              settles.
            </>
          )}
        </p>
      </div>

      {/* Summary stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Collateral Value"
          value={`$${account.collateralValue.toLocaleString()}`}
          icon={<Icon name="streamline:money-safe-vault-saving-combo-payment-safe-money-combination-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Debt to Repay"
          value={`$${account.outstandingDebt.toLocaleString()}`}
          icon={<Icon name="streamline:heart-rate-pulse-graph" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Profit/Loss"
          value={`${profitLoss >= 0 ? "+" : ""}$${profitLoss.toLocaleString()}`}
          className={profitLoss >= 0 ? "text-green-600" : "text-red-600"}
          icon={<Icon name="streamline:graph-bar-increase" className="h-5 w-5 text-muted-foreground" />}
        />
        {isVerifiedBuyerFlow ? (
          <StatCard
            title="Facility compliance"
            value="Passed"
            className="text-green-600"
            icon={<Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-5 w-5 text-muted-foreground" />}
            subtitle="Mock: you are eligible for this facility"
          />
        ) : (
          <StatCard
            title="Finder's Fee"
            value={`$${account.findersFee.toLocaleString()}`}
            className={
              account.feeGuaranteed ? "text-green-600" : "text-amber-600"
            }
            icon={<Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="h-5 w-5 text-muted-foreground" />}
            subtitle={
              account.feeGuaranteed
                ? "Facility-guaranteed when applicable; paid after auction settles"
                : "Not guaranteed; paid after auction if applicable"
            }
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left column */}
        <div className="space-y-6">
          {/* Transaction Flow */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle>Transaction Flow</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-4">
              {!isConnected ? (
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                  <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {isVerifiedBuyerFlow
                      ? "Connect your wallet to execute a direct liquidation."
                      : "Connect your wallet to trigger this liquidation."}
                  </p>
                  <Button onClick={connect}>Connect Wallet</Button>
                </div>
              ) : isVerifiedBuyerFlow ? (
                <>
                  <div className="flex items-center gap-4 rounded-xl border border-border p-4">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0 ${
                        approveState === "done" ? "bg-green-100" : "bg-muted"
                      }`}
                    >
                      {approveState === "done" ? (
                        <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-5 w-5 text-green-600" />
                      ) : (
                        <span className="text-sm font-bold text-muted-foreground">
                          1
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">
                        Approve settlement
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Allow the facility to pull USDC from your wallet up to
                        the outstanding debt
                      </p>
                    </div>
                    <Button
                      disabled={approveState !== "idle" || !hasEnoughBalance}
                      onClick={handleApproveVerified}
                    >
                      {approveState === "loading" ? (
                        <>
                          <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-1.5 animate-spin" />{" "}
                          Approving...
                        </>
                      ) : approveState === "done" ? (
                        <>
                          <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-4 w-4 mr-1.5" /> Approved
                        </>
                      ) : (
                        "Approve"
                      )}
                    </Button>
                  </div>

                  <div className="flex items-center gap-4 rounded-xl border border-border p-4">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0 ${
                        liquidateState === "done" ? "bg-green-100" : "bg-muted"
                      }`}
                    >
                      {liquidateState === "done" ? (
                        <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-5 w-5 text-green-600" />
                      ) : (
                        <span className="text-sm font-bold text-muted-foreground">
                          2
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">Liquidate</p>
                      <p className="text-xs text-muted-foreground">
                        You repay the margin account&apos;s debt from your
                        balance; collateral and margin-account ownership
                        transfer to you
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      disabled={
                        approveState !== "done" ||
                        liquidateState !== "idle" ||
                        !hasEnoughBalance
                      }
                      onClick={handleLiquidateVerified}
                    >
                      {liquidateState === "loading" ? (
                        <>
                          <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-1.5 animate-spin" />{" "}
                          Executing...
                        </>
                      ) : (
                        "Liquidate"
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Available only when you meet this facility&apos;s compliance
                    (same checks as the borrower flow on the credit facility
                    page).
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-4 rounded-xl border border-border p-4">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0 ${
                        liquidateState === "done" ? "bg-green-100" : "bg-muted"
                      }`}
                    >
                      {liquidateState === "done" ? (
                        <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-5 w-5 text-green-600" />
                      ) : (
                        <span className="text-sm font-bold text-muted-foreground">
                          1
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">
                        Trigger Liquidation
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Trigger DDF liquidation; finder&apos;s fee (if any) pays
                        out after the auction settles
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      disabled={liquidateState !== "idle"}
                      onClick={handleLiquidateSearcher}
                    >
                      {liquidateState === "loading" ? (
                        <>
                          <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-1.5 animate-spin" />{" "}
                          Triggering...
                        </>
                      ) : (
                        "Liquidate"
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Account Details */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle>Account Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="space-y-4 text-sm">
                <DetailRow label="Borrower" value={account.borrower} mono />
                <DetailRow label="Facility" value={account.facility} />
                <DetailRow
                  label="Collateral"
                  value={`${account.collateralAmount} ${account.token}`}
                />
                <DetailRow
                  label="Health Factor"
                  value={account.healthFactor.toFixed(3)}
                  valueClassName={
                    account.healthFactor < 1
                      ? "text-red-600 font-bold"
                      : account.healthFactor < 1.3
                        ? "text-amber-600 font-bold"
                        : "font-bold"
                  }
                />
                <DetailRow
                  label="Time in Default"
                  value={account.timeInDefault}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Your Balance (verified buyer flow only) */}
          {isVerifiedBuyerFlow && (
            <Card>
              <CardHeader className="p-6">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4" />
                  Your Balance
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-4">
                {!isConnected ? (
                  <p className="text-sm text-muted-foreground">
                    Connect wallet to view balance.
                  </p>
                ) : (
                  <>
                    <div className="text-sm">
                      <span className="text-muted-foreground">
                        USDC Balance
                      </span>
                      <div className="text-xl font-bold mt-1">
                        ${WALLET_BALANCE.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">
                        Required Amount
                      </span>
                      <div className="text-xl font-bold mt-1">
                        ${account.outstandingDebt.toLocaleString()}
                      </div>
                    </div>

                    {!hasEnoughBalance && (
                      <Alert variant="destructive">
                        <Icon name="streamline:warning-triangle" className="h-4 w-4" />
                        <AlertDescription>
                          Insufficient balance. You need $
                          {shortfall.toLocaleString()} more USDC.
                        </AlertDescription>
                      </Alert>
                    )}

                    {hasEnoughBalance && (
                      <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800 flex items-center gap-2">
                        <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-3.5 w-3.5" />
                        Sufficient balance to execute
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Liquidation Info */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="flex items-center gap-2 text-base">
                <Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-4 w-4" />
                How It Works
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {isVerifiedBuyerFlow ? (
                <ol className="space-y-3 text-xs text-muted-foreground list-decimal list-inside">
                  <li>
                    You must pass this credit facility&apos;s compliance (same
                    checks as borrowing: KYC, OnchainID, claims, policy).
                  </li>
                  <li>
                    <span className="font-medium text-foreground">
                      You pay the debt:
                    </span>{" "}
                    approve and send settlement (e.g. USDC) so the facility can
                    pull funds to cover the margin account&apos;s outstanding
                    debt.
                  </li>
                  <li>
                    <span className="font-medium text-foreground">
                      Ownership transfers:
                    </span>{" "}
                    after execution, collateral and the margin position (e.g.
                    margin account NFT) are yours - this is a direct
                    liquidation, not a DDF auction.
                  </li>
                  <li>
                    No finder&apos;s fee / searcher bounty; economics are
                    collateral value minus debt you repaid (plus gas).
                  </li>
                </ol>
              ) : (
                <ol className="space-y-3 text-xs text-muted-foreground list-decimal list-inside">
                  <li>
                    Call liquidate on the DDF contract to trigger the
                    liquidation
                  </li>
                  <li>
                    The DDF handles collateral seizure and debt repayment
                    through its auction process
                  </li>
                  <li>
                    A finder&apos;s fee (bounty), if configured by the facility,
                    is paid to your wallet after the DDF auction settles.
                  </li>
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  icon,
  className,
  subtitle,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  className?: string;
  subtitle?: string;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">
          {title}
        </span>
        {icon}
      </div>
      <div className={`mt-2 text-2xl sm:text-3xl font-bold ${className ?? ""}`}>
        {value}
      </div>
      {subtitle && (
        <div className="mt-1 text-xs text-muted-foreground">{subtitle}</div>
      )}
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
  valueClassName,
}: {
  label: string;
  value: string;
  mono?: boolean;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`${mono ? "font-mono text-xs" : "font-medium"} ${valueClassName ?? ""}`}
      >
        {value}
      </span>
    </div>
  );
}
